import { useCallback, useMemo, useState } from "react";
import type { Market } from "@module-bindings/types";
import { formatAssetLabel } from "@/lib/marketUtils";

export type TradeSide = "buy" | "sell";
export type OrderType = "market" | "limit";

function getMarketUrlState(market: Market | null, tradeSide: TradeSide) {
  const params = new URLSearchParams(window.location.search);

  if (market) {
    const baseLabel = formatAssetLabel(market.tradingPair.base);
    const quoteLabel = formatAssetLabel(market.tradingPair.quote);
    params.set("sell", tradeSide === "sell" ? baseLabel : quoteLabel);
    params.set("buy", tradeSide === "sell" ? quoteLabel : baseLabel);
  } else {
    params.delete("sell");
    params.delete("buy");
  }

  const qs = params.toString();
  return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
}

export function useTradingViewState(markets: readonly Market[]) {
  const [orderType, setOrderType] = useState<OrderType>("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [initialUrlSell] = useState(() => new URLSearchParams(window.location.search).get("sell"));
  const [initialUrlBuy] = useState(() => new URLSearchParams(window.location.search).get("buy"));
  const initialMarket = useMemo(() => {
    if (!initialUrlSell || !initialUrlBuy) return null;

    return markets.find((market) => {
      const baseLabel = formatAssetLabel(market.tradingPair.base);
      const quoteLabel = formatAssetLabel(market.tradingPair.quote);

      return (
        (baseLabel === initialUrlSell && quoteLabel === initialUrlBuy) ||
        (quoteLabel === initialUrlSell && baseLabel === initialUrlBuy)
      );
    }) ?? null;
  }, [initialUrlBuy, initialUrlSell, markets]);

  const [selectedMarketKey, setSelectedMarketKey] = useState<string | null>(null);
  const [tradeSideOverride, setTradeSideOverride] = useState<TradeSide | null>(null);

  const selectedMarket = useMemo(() => {
    if (selectedMarketKey) {
      const explicitMarket = markets.find((market) => market.key === selectedMarketKey);
      if (explicitMarket) return explicitMarket;
    }

    return initialMarket ?? markets[0] ?? null;
  }, [initialMarket, markets, selectedMarketKey]);

  const tradeSide = useMemo<TradeSide>(() => {
    if (tradeSideOverride) return tradeSideOverride;
    if (!initialMarket || !initialUrlSell) return "sell";
    const baseLabel = formatAssetLabel(initialMarket.tradingPair.base);
    return initialUrlSell === baseLabel ? "sell" : "buy";
  }, [initialMarket, initialUrlSell, tradeSideOverride]);

  const syncUrl = useCallback((market: Market | null, nextTradeSide: TradeSide) => {
    window.history.replaceState(null, "", getMarketUrlState(market, nextTradeSide));
  }, []);

  const handleSelectMarket = useCallback((market: Market | null) => {
    if (!market) return;
    setSelectedMarketKey(market.key);
    syncUrl(market, tradeSide);
  }, [syncUrl, tradeSide]);

  const handleTradeSideChange = useCallback((nextTradeSide: TradeSide) => {
    setTradeSideOverride(nextTradeSide);
    syncUrl(selectedMarket, nextTradeSide);
  }, [selectedMarket, syncUrl]);

  const handleSelectPrice = useCallback((price: number) => {
    setOrderType("limit");
    setLimitPrice(price.toFixed(6).replace(/0+$/, "").replace(/\.$/, ""));
  }, []);

  return {
    selectedMarket,
    handleSelectMarket,
    tradeSide,
    setTradeSide: handleTradeSideChange,
    orderType,
    setOrderType,
    limitPrice,
    setLimitPrice,
    handleSelectPrice,
  };
}
